<template>
  <div class="saas-listing-page">
    <div
      v-if="page === 1 && !$route.params.category"
      class="saas-blog-listing-head"
    >
      <div class="saas-blog-listing-head__wrapper">
        <div class="saas-blog-listing-head__container">
          <h1 class="saas-blog-listing-head__title">
            <strong> The latest insights and updates</strong> from Baserow
          </h1>

          <p class="saas-blog-listing-head__description">
            Get valuable resources, invites to events, and behind-the-scenes
            insight into what we’re doing.
          </p>

          <NewsletterSignup size="xlarge"></NewsletterSignup>
        </div>

        <div class="saas-blog-listing-head__blog-card">
          <BlogPostCard
            v-if="blogPosts.length"
            :post="blogPosts[0]"
          ></BlogPostCard>
        </div>
      </div>
    </div>
    <div ref="body" class="saas-listing-page__body">
      <div class="saas-listing-page__sidebar saas-listing-page__sidebar--small">
        <div class="saas-listing-page__sidebar-sticky">
          <BlogPostCategorySelect
            :categories="categoriesWithAll"
            :selected-category="selectedCategory"
            @switch-category="switchCategory"
          ></BlogPostCategorySelect>
        </div>
      </div>

      <div class="saas-listing-page__templates">
        <div>
          <h2
            v-if="$route.params.category"
            class="saas-listing-page__templates-title"
          >
            {{ categoryName }}
          </h2>
          <!--
          <div v-if="selectedCategory === 'all'">
            <h2 class="saas-listing-page__templates-title">Featured posts</h2>

            <div class="saas-blog-cards">
              <BlogPostCard
                v-for="(post, index) in blogPosts"
                :key="post.slug"
                :post="post"
                :direction="index === 0 ? 'horizontal' : 'vertical'"
              ></BlogPostCard>
            </div>
          </div>
          -->

          <div>
            <div class="saas-blog-cards">
              <BlogPostCard
                v-for="post in blogPosts"
                :key="post.slug"
                :post="post"
              ></BlogPostCard>

              <NewsletterSignupCard
                v-if="blogPosts.length % 2 !== 0"
              ></NewsletterSignupCard>
            </div>
          </div>
        </div>

        <SaaSPaginator
          v-if="totalPages > 1"
          :current-page="page"
          :total-pages="totalPages"
          :target-page-name="'blog-filtered'"
          :extra-params="{ category: selectedCategory }"
        >
        </SaaSPaginator>

        <!--
        <Button class="saas-listing-page__load-more" size="xlarge"
          >Load more</Button
        >

        <div class="saas-cta-box page-community__cta-box-with-margin">
          <div>
            <span class="saas-cta-box__title">Interested in reading more?</span>
            <p class="saas-cta-box__text">
              Read blog posts from years past in our Baserow archive.
            </p>
          </div>

          <div class="saas-cta-box__buttons">
            <Button type="secondary"> Explore the archive </Button>
          </div>
        </div>
        -->
      </div>
    </div>

    <FooterJoin></FooterJoin>
  </div>
</template>

<script>
import { clone } from '@baserow/modules/core/utils/object'
import BlogService from '@saas/services/blog'
import FooterJoin from '@saas/components/footer/FooterJoin'
import NewsletterSignup from '@saas/components/NewsletterSignup'
import NewsletterSignupCard from '@saas/components/NewsletterSignupCard'
import BlogPostCard from '@saas/components/blog/BlogPostCard'
import BlogPostCategorySelect from '@saas/components/blog/BlogPostCategorySelect'
import SaaSPaginator from '@saas/components/SaaSPaginator'

const BLOG_POST_PAGE_SIZE = 10
export default {
  components: {
    NewsletterSignup,
    NewsletterSignupCard,
    FooterJoin,
    BlogPostCard,
    BlogPostCategorySelect,
    SaaSPaginator,
  },
  layout: 'saas',
  async asyncData({ params, app }) {
    try {
      const page = Number(params.page) || 1
      const selectedCategory = params.category || 'all'
      const serverCategoryFilter =
        selectedCategory === 'all' ? false : selectedCategory
      const { data } = await BlogService(app.$client).getSummariesPage(page, {
        category: serverCategoryFilter,
        includeCategories: true,
      })
      return {
        blogPosts: data.results,
        categories: data.categories || [],
        page,
        blogPostsTotalCount: data.count,
        selectedCategory,
      }
    } catch (e) {
      return {
        blogPosts: [],
        categories: [],
        page: 1,
        blogPostsTotalCount: 0,
        selectedCategory: 'all',
      }
    }
  },

  head() {
    let title = `${this.categoryName} Articles – Page ${this.page} | Baserow Blog`
    let description = `Browse page ${this.page} of our ${this.categoryName} posts on the Baserow Blog. Discover tutorials, insights, and updates for no-code enthusiasts and developers.`
    if (this.selectedCategory === 'all' && this.page === 1) {
      title = 'Baserow Blog: Productivity Tips, Tutorials & Latest News'
      description =
        'An overview of the latest changes, updates tips and tricks related to Baserow the open source online database tool.'
    }

    // Based on this configuration, the `/blog/` path renders the same page as
    // `/blog/category/all/1`. Same for `/blog/category/test` and
    // `/blog/category/test/1`. To avoid duplicate pages we're setting the canonical
    // to the shortest path.
    const params = clone(this.$route.params)
    let name = 'blog-filtered'
    if (params.page === '1' || !params.page) {
      delete params.page
      if (params.category === 'all' || !params.category) {
        name = 'blog'
      }
    }
    const canonicalPath = this.$router.resolve({ name, params }).href

    return {
      title,
      meta: [
        {
          name: 'description',
          content: description,
        },
      ],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          href: this.$config.PUBLIC_WEB_FRONTEND_URL + canonicalPath,
        },
      ],
    }
  },

  computed: {
    categoriesWithAll() {
      return [
        {
          slug: 'all',
          name: 'All topics',
        },
        ...this.categories,
      ]
    },
    totalPages() {
      return Math.ceil(this.blogPostsTotalCount / BLOG_POST_PAGE_SIZE)
    },
    categoryName() {
      const category = this.categoriesWithAll.find(
        (c) => c.slug === this.selectedCategory
      )
      return category.name
    },
  },
  methods: {
    switchCategory(category) {
      this.$router.push({
        name: 'blog-filtered',
        params: {
          category,
        },
      })
    },
  },
}
</script>
